<script>
import RawData from "@/components/widgets/raw-data.vue";
import ProposalItemExpenditure from "@/components/widgets/proposal-item-expenditure.vue";
import { groupBy } from "lodash";

export default {
    name: "ProposalByKpi",
    components: {
        RawData,
        ProposalItemExpenditure,
    },
    props: {
        targets: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        du: {
            type: Object,
            required: true,
        },
        campus: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        viewFrom: {
            type: String,
            default: "planning",
        },
        filter: {
            type: String,
            default: "",
        },
        campIndex: {
            type: Number,
            default: 0,
        },
        duIndex: {
            type: Number,
            default: 0,
        },
        totalCampuses: {
            type: Number,
            default: 1,
        },
        department: {
            type: [Object, String, Boolean],
            default: null,
        },
        selectedTemplate: {
            type: Object,
            default: () => ({}),
        },
        budgets: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tabIndex: 0,
            tabSelect: "Strategic Priority",
            kpiSearch: "",
            groupedList: [],
        };
    },
    computed: {
        isActive() {
            return this.type === "active";
        },
        isShortListed() {
            return this.type === "shortlisted";
        },
        isParked() {
            return this.type === "parked";
        },
        isApproved() {
            return this.type === "approved";
        },
        tabOptions() {
            const defaults = [
                "Strategic Priority",
                "Core Functions",
                "Mandatory Support Function",
            ];
            const others = [];
            (this.targets || []).forEach((t) => {
                if (t.indicatorItemClassification) {
                    const isDefault = defaults.some((d) =>
                        t.indicatorItemClassification
                            .toUpperCase()
                            .includes(d.split(" ")[0].toUpperCase())
                    );
                    if (!isDefault && !others.includes(t.indicatorItemClassification)) {
                        others.push(t.indicatorItemClassification);
                    }
                }
            });
            return [...defaults, ...others];
        },
    },
    watch: {
        targets: {
            immediate: true,
            handler(val) {
                this.groupedList = this.groupTargets(val || []);
                // Set initial tabSelect to the first tab that has data
                for (const tab of this.tabOptions) {
                    if (this.checkTab(tab)) {
                        this.tabSelect = tab;
                        break;
                    }
                }
            },
        },
    },
    methods: {
        groupTargets(rawTargets) {
            const array = [];
            const groupedByIndicatorItem = groupBy(rawTargets, "indicatorItem");
            for (const indicatorItem in groupedByIndicatorItem) {
                const itemsUnderKpi = groupedByIndicatorItem[indicatorItem];
                const groupedByClassification = groupBy(
                    itemsUnderKpi,
                    "indicatorItemClassification"
                );
                for (const classification in groupedByClassification) {
                    const details = groupedByClassification[classification].map((item) => {
                        const apis = (item.apis || []).map((api) => ({
                            ...api,
                            showResources: api.showResources || false,
                            resourceLoading: api.resourceLoading || false,
                            resources: api.resources || [],
                            statusUpdate: api.statusUpdate || "",
                            searchItem: api.searchItem || "",
                        }));
                        return {
                            ...item,
                            showApis: item.showApis || false,
                            apis: apis,
                        };
                    });

                    array.push({
                        indicatorItemName: indicatorItem,
                        indicatorItemClassification: classification,
                        details: details,
                        showTargets: false,
                    });
                }
            }
            return array;
        },
        checkTab(type) {
            if (!this.groupedList || !this.groupedList.length) return false;
            return this.groupedList.some((item) => {
                if (!item.indicatorItemClassification) return false;
                if (type === "Strategic Priority") {
                    return item.indicatorItemClassification
                        .toUpperCase()
                        .includes("STRATEGIC");
                } else if (type === "Core Functions") {
                    return item.indicatorItemClassification
                        .toUpperCase()
                        .includes("CORE");
                } else if (type === "Mandatory Support Function") {
                    return item.indicatorItemClassification
                        .toUpperCase()
                        .includes("MANDATORY");
                }
                return (
                    item.indicatorItemClassification.toUpperCase() ===
                    type.toUpperCase()
                );
            });
        },
        filteredTargets(type) {
            let data = this.groupedList.filter((item) => {
                if (!item.indicatorItemClassification) return false;
                if (type === "Strategic Priority") {
                    return item.indicatorItemClassification
                        .toUpperCase()
                        .includes("STRATEGIC");
                } else if (type === "Core Functions") {
                    return item.indicatorItemClassification
                        .toUpperCase()
                        .includes("CORE");
                } else if (type === "Mandatory Support Function") {
                    return item.indicatorItemClassification
                        .toUpperCase()
                        .includes("MANDATORY");
                }
                return (
                    item.indicatorItemClassification.toUpperCase() ===
                    type.toUpperCase()
                );
            });

            const query = (this.kpiSearch || "").trim().toLowerCase();
            if (query) {
                data = data.filter((item) => {
                    if (
                        item.indicatorItemName &&
                        item.indicatorItemName.toLowerCase().includes(query)
                    ) {
                        return true;
                    }
                    return item.details.some((y) => {
                        const matchDesc =
                            (y.description &&
                                y.description.toLowerCase().includes(query)) ||
                            (y.planningMainTarget &&
                                y.planningMainTarget.toLowerCase().includes(query));
                        const matchApi = (y.apis || []).some(
                            (a) => a.name && a.name.toLowerCase().includes(query)
                        );
                        return matchDesc || matchApi;
                    });
                });
            }
            return data;
        },
        toggleTargets(kpi) {
            kpi.showTargets = !kpi.showTargets;
        },
        toggleApis(target) {
            target.showApis = !target.showApis;
        },
        async toggleResources(api) {
            const nextState = !api.showResources;
            this.$set(api, "showResources", nextState);
            if (nextState && (!api.resources || !api.resources.length)) {
                this.$set(api, "resourceLoading", true);
                try {
                    const res = await this.$store.dispatch(
                        "planningapiresource/GetPlanningApiResourcesByApi",
                        api.id
                    );
                    const response = res && res.data !== undefined ? res.data : res;
                    if (response && response.error) {
                        if (this.showToast) {
                            this.showToast(response.errorMessage || response.message || "Failed to fetch resources.", "error");
                        }
                        this.$set(api, "resources", []);
                    } else {
                        const list = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
                        list.forEach((item) => {
                            let budgetInfo = this.budgets.find((x) => x.id == item.accountGroupId);
                            item.totalQuantity = item.planningApiResourceDetails.reduce((pv, cv) => {
                                return (parseFloat(pv) + parseFloat(cv.quantity));
                            }, 0);
                            item.financeTemplateBudgetItem = {
                                fundSource: budgetInfo?.fundSource || "",
                                fundClass: budgetInfo?.fundClassification?.classification || "",
                                fundClassification: budgetInfo?.fundClassification || null,
                                groupName: budgetInfo?.groupName || "",
                                accountGroup: budgetInfo?.groupName || "",
                                accountId: budgetInfo?.id || item.accountGroupId,
                                id: budgetInfo?.id || item.accountGroupId
                            };
                            item.showRow = true;
                            item.showChild = true;
                            item.filterKey = `${item.itemName} ${item.description}`;
                            item.status = null;
                        });
                        this.$set(api, "resources", list);
                    }
                } catch (err) {
                    if (this.showToast) {
                        this.showToast("Failed to fetch resources.", "error");
                    }
                    this.$set(api, "resources", []);
                } finally {
                    this.$set(api, "resourceLoading", false);
                }
            }
        },
        hasStatus(api, status) {
            if (!api || !api.status) return false;
            return api.status.toLowerCase().includes(status.toLowerCase());
        },
        filteredItems(items, filter) {
            let data = items || [];
            if (filter && filter.trim() !== '') {
                data = data.filter((x) => {
                    const key = x.filterKey || `${x.itemName || ''} ${x.description || ''}`;
                    return key.toUpperCase().includes(filter.toUpperCase());
                });
            }
            return data;
        },
        updateProposalStatus({ proposalId, du, campus, status }) {
            if (status === "approve") {
                this.$emit("approve-proposal", { proposalId, du, campus });
            } else if (status === "return") {
                this.$emit("return-proposal", { proposalId, du, campus });
            } else if (status === "shortlist") {
                this.$emit("shortlist-proposal", { proposalId, du, campus });
            } else if (status === "park") {
                this.$emit("park-proposal", { proposalId, du, campus });
            }
        },
        viewAttachments(targets) {
            let apis = [];
            for (let target of targets) {
                if (target.apis && target.apis.length) {
                    target.apis.forEach((api) => {
                        apis.push({
                            id: api.id,
                            name: api.name
                        })
                    })
                }
            }
            this.$emit("view-attachments", apis);
        },
    },
};
</script>

<template>
    <div class="proposal-by-kpi">
        <b-card no-body
            :style="{ zIndex: `${totalCampuses - campIndex}${campus.dus ? campus.dus.length - duIndex : 1}` }"
            class="mb-2 shadow-none mt-2">
            <template v-if="loading">
                <div class="d-flex justify-content-center align-items-center py-5">
                    <b-spinner variant="primary" label="Loading Targets..."></b-spinner>
                    <span class="ms-2">Loading KPI targets...</span>
                </div>
            </template>
            <template v-else-if="!targets.length">
                <div class="text-center py-4">
                    <p class="text-muted mb-0">No KPI targets found for this Delivery Unit.</p>
                </div>
            </template>
            <template v-else>
                <div class="position-relative">
                    <b-tabs content-class="pt-3 pb-3 text-muted" v-model="tabIndex" pills>
                        <div v-for="(type, index) in tabOptions" :key="index">
                            <b-tab v-if="checkTab(type)" @click="kpiSearch = ''; tabSelect = type;">
                                <template v-slot:title>
                                    <span class="d-inline-block d-sm-none">
                                        <i class="mdi mdi-clock-time-four-outline"></i>
                                    </span>
                                    <span class="d-none d-sm-inline-block">{{ type }}</span>
                                </template>
                                <div>
                                    <!-- Filter Search Input -->
                                    <div class="mb-3 d-flex justify-content-between align-items-center">
                                        <div class="w-300px">
                                            <b-form-input autocomplete="off" v-model="kpiSearch" type="search"
                                                placeholder="Search KPI, Target, API..."
                                                class="form-control form-control-sm"></b-form-input>
                                        </div>
                                    </div>

                                    <b-card-body class="p-0">
                                        <div v-if="filteredTargets(type).length === 0" class="text-center py-3">
                                            <h5 v-if="kpiSearch">Search not found</h5>
                                            <h5 v-else>No data available</h5>
                                        </div>

                                        <!-- KPI Cards (Grouped by indicatorItem) -->
                                        <div role="tablist" v-for="(kpi, kpiIndex) in filteredTargets(type)"
                                            :key="`kpi-${kpi.indicatorItemName}-${kpiIndex}`">
                                            <b-card no-body class="mb-2 border shadow-none"
                                                :style="{ 'z-index': filteredTargets(type).length - kpiIndex }">
                                                <b-card-header header-tag="header" role="tab" class="py-2">
                                                    <div class="d-flex justify-content-between align-items-center cursor-pointer"
                                                        @click="toggleTargets(kpi)">
                                                        <div class="me-3">
                                                            <div>
                                                                <b class="text-white font-size-15">
                                                                    {{ kpi.indicatorItemName }}
                                                                </b>
                                                            </div>
                                                            <small class="text-muted">
                                                                Targets Available ({{ kpi.details.length }})
                                                            </small>
                                                        </div>
                                                        <div class="d-flex align-items-center">
                                                            <span class="d-flex align-items-center">
                                                                <small v-if="!kpi.showTargets" class="text-muted">
                                                                    Click here to expand
                                                                </small>
                                                                <i class="bx bx-chevron-right rotate font-size-18 ms-2"
                                                                    :class="{ 'rotate-90': kpi.showTargets }"></i>
                                                            </span>
                                                        </div>
                                                    </div>
                                                </b-card-header>

                                                <!-- Expanded Targets Collapse -->
                                                <b-collapse :visible="kpi.showTargets"
                                                    :accordion="`kpi-accordion-${type}-${kpiIndex}`" role="tabpanel">
                                                    <b-card-body class="ps-3 pe-2 pt-3 pb-2">
                                                        <div class="d-flex align-items-center mb-2">
                                                            <h6 class="card-title text-muted mb-0">Target List</h6>
                                                        </div>

                                                        <!-- Target List inside KPI -->
                                                        <div class="card mb-2 border shadow-none"
                                                            v-for="(y, yIndex) in kpi.details"
                                                            :key="`target-${y.id}-${yIndex}`"
                                                            :style="{ 'z-index': kpi.details.length + 10 - yIndex }">
                                                            <RawData :data="y" v-if="$store.state.decrypt.isAdmin()" />
                                                            <b-card-header class="py-2">
                                                                <div
                                                                    class="d-flex justify-content-between flex-wrap gap-2">
                                                                    <!-- Target Header Left: Number & Description & Actions -->
                                                                    <div class="d-flex align-items-center">
                                                                        <div class="bg-success bg-gradient text-center me-2 py-1 px-2 position-relative rounded"
                                                                            style="min-width: 30px">
                                                                            <h4
                                                                                class="mb-0 text-white font-weight-bold">
                                                                                {{ yIndex + 1 }}
                                                                            </h4>
                                                                        </div>
                                                                        <div>
                                                                            <h5
                                                                                class="card-title font-size-15 mb-1 text-info">
                                                                                {{ y.description || y.planningMainTarget
                                                                                }}
                                                                            </h5>
                                                                            <div
                                                                                class="d-flex align-items-center gap-2 flex-wrap">
                                                                                <div>
                                                                                    <small>
                                                                                        <a href="javascript:void(0);"
                                                                                            class="text-warning d-flex align-items-center hover-underline font-weight-bold"
                                                                                            @click="toggleApis(y)">
                                                                                            <i
                                                                                                class="bx bx-list-ul me-1"></i>
                                                                                            {{ y.showApis ? 'Hide' :
                                                                                                'View'
                                                                                            }} API's ({{ (y.apis ||
                                                                                                []).length }})
                                                                                        </a>
                                                                                    </small>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <!-- Target Header Right: Cascaded Target & Quarters Table -->
                                                                    <div class="d-flex">
                                                                        <table
                                                                            class="table table-sm table-bordered mb-0 text-center font-size-12">
                                                                            <template
                                                                                v-if="y.cascadedTargetText || (y.planningMainTarget && y.planningMainTarget.indicatorItem && y.planningMainTarget.indicatorItem.allowText)">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th class="px-2">Cascaded Target
                                                                                        </th>
                                                                                        <th class="px-2">Q1</th>
                                                                                        <th class="px-2">Q2</th>
                                                                                        <th class="px-2">Q3</th>
                                                                                        <th class="px-2">Q4</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <tr>
                                                                                        <td
                                                                                            class="text-warning font-weight-bold px-2">
                                                                                            {{ y.cascadedTargetText ||
                                                                                                y.cascadedTarget }}</td>
                                                                                        <td class="text-warning px-2">{{
                                                                                            y.q1Text || y.q1 }}</td>
                                                                                        <td class="text-warning px-2">{{
                                                                                            y.q2Text || y.q2 }}</td>
                                                                                        <td class="text-warning px-2">{{
                                                                                            y.q3Text || y.q3 }}</td>
                                                                                        <td class="text-warning px-2">{{
                                                                                            y.q4Text || y.q4 }}</td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            </template>
                                                                            <template v-else>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style="min-width: 110px;"
                                                                                            class="px-2">Cascaded Target
                                                                                        </th>
                                                                                        <th style="min-width: 60px;"
                                                                                            class="px-2">Q1</th>
                                                                                        <th style="min-width: 60px;"
                                                                                            class="px-2">Q2</th>
                                                                                        <th style="min-width: 60px;"
                                                                                            class="px-2">Q3</th>
                                                                                        <th style="min-width: 60px;"
                                                                                            class="px-2">Q4</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <tr>
                                                                                        <td
                                                                                            class="text-warning font-weight-bold px-2">
                                                                                            {{ y.planningMainTarget &&
                                                                                                y.planningMainTarget.isPercentage
                                                                                                ? `${y.cascadedTarget}%` :
                                                                                                y.cascadedTarget }}
                                                                                        </td>
                                                                                        <td class="text-warning px-2">
                                                                                            {{ y.planningMainTarget &&
                                                                                                y.planningMainTarget.isPercentage
                                                                                                ? `${y.q1}%` : y.q1 }}
                                                                                        </td>
                                                                                        <td class="text-warning px-2">
                                                                                            {{ y.planningMainTarget &&
                                                                                                y.planningMainTarget.isPercentage
                                                                                                ? `${y.q2}%` : y.q2 }}
                                                                                        </td>
                                                                                        <td class="text-warning px-2">
                                                                                            {{ y.planningMainTarget &&
                                                                                                y.planningMainTarget.isPercentage
                                                                                                ? `${y.q3}%` : y.q3 }}
                                                                                        </td>
                                                                                        <td class="text-warning px-2">
                                                                                            {{ y.planningMainTarget &&
                                                                                                y.planningMainTarget.isPercentage
                                                                                                ? `${y.q4}%` : y.q4 }}
                                                                                        </td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            </template>
                                                                        </table>
                                                                    </div>
                                                                </div>

                                                                <!-- List of APIs Section (Visible when View API's is clicked) -->
                                                                <div v-if="y.showApis" class="mt-3">
                                                                    <hr class="my-2" />
                                                                    <div
                                                                        class="d-flex justify-content-between align-items-center mb-2">
                                                                        <h6 class="mb-0 text-white font-size-14">
                                                                            <i class="bx bx-file me-1"></i>List of API's
                                                                        </h6>
                                                                        <a href="javascript:void(0);"
                                                                            class="font-size-18 text-white"
                                                                            @click="y.showApis = false">
                                                                            <i class="bx bx-x"></i>
                                                                        </a>
                                                                    </div>

                                                                    <div class="text-center py-3"
                                                                        v-if="!y.apis || y.apis.length === 0">
                                                                        <span class="text-muted">No API(s) found for
                                                                            this
                                                                            target.</span>
                                                                    </div>

                                                                    <div class="table-responsive" v-else>
                                                                        <table
                                                                            class="table table-bordered table-sm align-middle mb-0 font-size-13">
                                                                            <thead class="table-dark">
                                                                                <tr>
                                                                                    <th>API Description</th>
                                                                                    <th width="170px" class="text-end">
                                                                                        Total
                                                                                        Amount</th>
                                                                                    <th width="280px"
                                                                                        class="text-center">
                                                                                        Action / Status</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                <template v-for="w in y.apis">
                                                                                    <tr :key="`api-${w.id}`">
                                                                                        <td>
                                                                                            <div>
                                                                                                <span
                                                                                                    class="text-white font-weight-bold">{{
                                                                                                        w.name }}</span>
                                                                                                <span
                                                                                                    v-if="$store.state.decrypt.isAdmin()"
                                                                                                    class="ms-1 cursor-pointer"
                                                                                                    title="API Logs"
                                                                                                    @click.stop="$emit('show-api-logs', w)">
                                                                                                    <i
                                                                                                        class="bx bx-receipt text-info"></i>
                                                                                                </span>
                                                                                            </div>
                                                                                            <small
                                                                                                class="d-block text-muted"
                                                                                                v-if="w.description">
                                                                                                [{{ w.description }}]
                                                                                            </small>
                                                                                            <!-- API Action Links -->
                                                                                            <div
                                                                                                class="d-flex gap-2 flex-wrap mt-1">
                                                                                                <small>
                                                                                                    <a href="javascript:void(0);"
                                                                                                        class="text-green hover-underline"
                                                                                                        @click.stop="toggleResources(w)">
                                                                                                        [ {{
                                                                                                            w.showResources
                                                                                                                ? 'Hide' :
                                                                                                                'View' }}
                                                                                                        Resources ]
                                                                                                    </a>
                                                                                                </small>
                                                                                                <small>
                                                                                                    <a href="javascript:void(0);"
                                                                                                        class="text-yellow hover-underline"
                                                                                                        @click.stop="$emit('show-api-attachments', w)">
                                                                                                        [ View
                                                                                                        Attachments ]
                                                                                                    </a>
                                                                                                </small>
                                                                                                <small>
                                                                                                    <a href="javascript:void(0);"
                                                                                                        class="text-white hover-underline"
                                                                                                        @click.stop="$emit('view-proposal-data', { props: w, status: type, du: du, campus: campus })">
                                                                                                        [ View Proposal
                                                                                                        ]
                                                                                                    </a>
                                                                                                </small>
                                                                                                <small>
                                                                                                    <a href="javascript:void(0);"
                                                                                                        class="text-info hover-underline"
                                                                                                        @click.stop="$emit('add-comment', { proposal: w, proposalId: w.id, du: du, campus: campus })">
                                                                                                        [ Add Comment ]
                                                                                                    </a>
                                                                                                </small>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td
                                                                                            class="text-end font-weight-bold text-warning align-top pt-2">
                                                                                            {{ formatCurrency(w.total)
                                                                                            }}
                                                                                        </td>
                                                                                        <td class="align-top pt-2">
                                                                                            <template
                                                                                                v-if="hasStatus(w, 'return')">
                                                                                                <div
                                                                                                    class="text-center">
                                                                                                    <span
                                                                                                        class="badge bg-warning text-white font-size-12">RETURNED</span>
                                                                                                </div>
                                                                                            </template>
                                                                                            <template v-else>
                                                                                                <div
                                                                                                    class="d-flex flex-column">
                                                                                                    <label
                                                                                                        class="mb-1 font-size-11">
                                                                                                        Status:
                                                                                                        <span
                                                                                                            class="ms-1 text-white font-weight-bold"
                                                                                                            v-if="w.status && w.status.toUpperCase() !== 'DRAFT'">
                                                                                                            {{ w.status
                                                                                                            }}
                                                                                                        </span>
                                                                                                        <span v-else
                                                                                                            class="ms-1 text-muted">
                                                                                                            Draft
                                                                                                        </span>
                                                                                                    </label>
                                                                                                    <div
                                                                                                        class="d-flex align-items-center gap-1">
                                                                                                        <select
                                                                                                            class="form-select form-select-sm"
                                                                                                            v-model="w.statusUpdate">
                                                                                                            <option
                                                                                                                value="">
                                                                                                                --Select
                                                                                                                status--
                                                                                                            </option>
                                                                                                            <template
                                                                                                                v-if="isActive || isShortListed || isParked">
                                                                                                                <option
                                                                                                                    value="approve"
                                                                                                                    class="text-green">
                                                                                                                    Approve
                                                                                                                </option>
                                                                                                            </template>
                                                                                                            <option
                                                                                                                value="return"
                                                                                                                class="text-warning">
                                                                                                                Return
                                                                                                            </option>
                                                                                                            <template
                                                                                                                v-if="isActive || isParked">
                                                                                                                <option
                                                                                                                    value="shortlist">
                                                                                                                    Add
                                                                                                                    to
                                                                                                                    Shortlist
                                                                                                                </option>
                                                                                                            </template>
                                                                                                            <template
                                                                                                                v-if="isActive || isShortListed || isApproved">
                                                                                                                <option
                                                                                                                    value="park">
                                                                                                                    Park
                                                                                                                </option>
                                                                                                            </template>
                                                                                                        </select>
                                                                                                        <button
                                                                                                            v-if="w.statusUpdate"
                                                                                                            type="button"
                                                                                                            class="btn btn-sm"
                                                                                                            :class="{
                                                                                                                'btn-success': w.statusUpdate === 'approve',
                                                                                                                'btn-warning': w.statusUpdate === 'return',
                                                                                                                'btn-info': w.statusUpdate === 'shortlist',
                                                                                                                'btn-secondary': w.statusUpdate === 'park'
                                                                                                            }" @click="updateProposalStatus({
                                                                                                                proposalId: w.id,
                                                                                                                du: du,
                                                                                                                campus: campus,
                                                                                                                status: w.statusUpdate
                                                                                                            })">
                                                                                                            <i
                                                                                                                class="bx bx-check"></i>
                                                                                                        </button>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </template>
                                                                                        </td>
                                                                                    </tr>

                                                                                    <!-- Expandable Resource Row when View Resources is active -->
                                                                                    <tr v-if="w.showResources"
                                                                                        :key="`api-res-${w.id}`">
                                                                                        <td colspan="3"
                                                                                            class="bg-dark p-2">
                                                                                            <div
                                                                                                class="table-responsive">
                                                                                                <ProposalItemExpenditure
                                                                                                    :api="w"
                                                                                                    :view-from="viewFrom"
                                                                                                    :department="department"
                                                                                                    @change-expenditure="$emit('change-expenditure', $event)"
                                                                                                    @assign-fund-source="$emit('assign-fund-source', { ...$event, campus: campus, campusId: campus && campus.id ? campus.id : null })" />
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                </template>
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </b-card-header>
                                                        </div>
                                                    </b-card-body>
                                                </b-collapse>
                                            </b-card>
                                        </div>
                                    </b-card-body>
                                </div>
                            </b-tab>
                        </div>
                    </b-tabs>
                    <div class="position-absolute" :style="{ top: 0, right: '5px' }">
                        <button class="btn btn-warning" @click="viewAttachments(targets)"><i
                                class="bx bx-paperclip"></i> View
                            Attachments</button>
                    </div>
                </div>
            </template>
        </b-card>
    </div>
</template>

<style scoped>
.rotate {
    transition: transform 0.2s ease-in-out;
}

.rotate-90 {
    transform: rotate(90deg);
}

.hover-underline:hover {
    text-decoration: underline;
}

.cursor-pointer {
    cursor: pointer;
}

.w-300px {
    width: 300px;
}
</style>
